export const API_URL = import.meta.env.VITE_API_URL ?? 'https://api.saysaybar.com'

export interface ImportResult {
	imported: number
	skipped: number
}

export class ApiError extends Error {
	readonly status: number

	constructor(message: string, status: number) {
		super(message)
		this.status = status
	}
}

export function errorMessage(error: unknown, fallback: string): string {
	return error instanceof ApiError ? error.message : fallback
}

export function isForbidden(error: unknown): boolean {
	return error instanceof ApiError && error.status === 403
}

export function isUnauthorized(error: unknown): boolean {
	return error instanceof ApiError && error.status === 401
}

export function isClientError(error: unknown): boolean {
	return error instanceof ApiError && error.status >= 400 && error.status < 500
}

async function failure(res: Response, path: string): Promise<Error> {
	const body: unknown = await res.json().catch(() => null)
	if (typeof body === 'object' && body !== null && 'error' in body && typeof body.error === 'string') {
		return new ApiError(body.error, res.status)
	}
	return new Error(`Request failed: ${path}`)
}

export async function request<T>(path: string, options?: RequestInit): Promise<T> {
	const res = await fetch(`${API_URL}${path}`, {
		...options,
		credentials: 'include',
		headers: { 'Content-Type': 'application/json', ...options?.headers },
	})
	if (!res.ok) throw await failure(res, path)
	return res.json() as Promise<T>
}

export async function requestText(path: string): Promise<string> {
	const res = await fetch(`${API_URL}${path}`, {
		credentials: 'include',
	})
	if (!res.ok) throw await failure(res, path)
	return res.text()
}

export async function requestBlob(path: string): Promise<Blob> {
	const res = await fetch(`${API_URL}${path}`, {
		credentials: 'include',
	})
	if (!res.ok) throw await failure(res, path)
	return res.blob()
}

export async function requestUpload<T>(path: string, file: File, fields: Record<string, string> = {}): Promise<T> {
	const formData = new FormData()
	formData.append('file', file)
	for (const [name, value] of Object.entries(fields)) formData.append(name, value)
	const res = await fetch(`${API_URL}${path}`, {
		method: 'POST',
		credentials: 'include',
		body: formData,
	})
	if (!res.ok) throw await failure(res, path)
	return res.json() as Promise<T>
}
